import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Box, Paper, Typography, TextField, Button, Alert, IconButton, InputAdornment, Container, CircularProgress, keyframes } from '@mui/material';
import { LockIcon, EyeIcon, EyeOffIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { hashPassword } from '../../utils/passwordUtils';
const floatingAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;
const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    logout
  } = useAuth();
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState<{
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setErrors({});
  };
  const togglePasswordVisibility = (field: 'old' | 'new' | 'confirm') => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };
  const validatePassword = (value: string) => {
    if (!value) return 'Password is required';
    const minLength = value.length >= 8;
    const maxLength = value.length <= 15;
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
    if (!minLength || !maxLength) {
      return 'Password must be between 8-15 characters';
    }
    if (!hasUppercase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowercase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecial) {
      return 'Password must contain at least one special character';
    }
    return '';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccess('');
    // Validation
    if (!formData.oldPassword) {
      setErrors({
        oldPassword: 'Current password is required'
      });
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    const newPasswordError = validatePassword(formData.newPassword);
    const confirmPasswordError = formData.newPassword !== formData.confirmPassword ? 'Passwords do not match' : '';
    if (newPasswordError || confirmPasswordError) {
      setErrors({
        newPassword: newPasswordError,
        confirmPassword: confirmPasswordError
      });
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    if (formData.oldPassword === formData.newPassword) {
      setErrors({
        newPassword: 'New password must be different from old password'
      });
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    setIsLoading(true);
    // Get users from localStorage
    const storedUsers = localStorage.getItem('users');
    if (!storedUsers) {
      setErrors({
        oldPassword: 'User data not found'
      });
      setIsLoading(false);
      return;
    }
    const users = JSON.parse(storedUsers);
    const currentUser = users.find((u: any) => u.id === user?.id);
    if (!currentUser) {
      setErrors({
        oldPassword: 'User not found'
      });
      setIsLoading(false);
      return;
    }
    // Verify old password
    const hashedOldPassword = hashPassword(formData.oldPassword);
    if (currentUser.password !== hashedOldPassword) {
      setErrors({
        oldPassword: 'Current password is incorrect'
      });
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      setIsLoading(false);
      return;
    }
    // Update password
    const hashedNewPassword = hashPassword(formData.newPassword);
    currentUser.password = hashedNewPassword;
    currentUser.firstLoginRequired = false;
    currentUser.passwordChangedAt = new Date().toISOString();
    // Save updated users
    localStorage.setItem('users', JSON.stringify(users));
    setSuccess('Password changed successfully! You will be logged out in 2 seconds...');
    // Logout after 2 seconds
    setTimeout(() => {
      logout();
      navigate('/login');
    }, 2000);
    setIsLoading(false);
  };
  return <Box sx={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    position: 'relative',
    px: 2,
    overflow: 'hidden'
  }} data-id="element-67">
      <Container maxWidth="sm" sx={{
      mb: 4,
      mt: 4,
      position: 'relative',
      zIndex: 1
    }} data-id="element-68">
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3
      }} data-id="element-69">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
          height: 80,
          marginBottom: 20
        }} data-id="element-70" />
          <Typography variant="subtitle1" sx={{
          color: '#6B7280',
          textAlign: 'center',
          fontWeight: 600,
          letterSpacing: '1px'
        }} data-id="element-71">
            Account Settings
          </Typography>
        </Box>

        <Paper elevation={0} sx={{
        p: {
          xs: 3,
          sm: 4
        },
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
        position: 'relative',
        animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : 'none'
      }} data-id="element-72">
          <Typography variant="h5" component="h2" sx={{
          mb: 1,
          textAlign: 'center',
          color: '#1F2937',
          fontWeight: 700
        }} data-id="element-73">
            Change Password
          </Typography>

          <Typography variant="body2" sx={{
          mb: 3,
          textAlign: 'center',
          color: 'text.secondary'
        }} data-id="element-74">
            Enter your current password and choose a new password
          </Typography>

          {success ? <Alert severity="success" sx={{
          mb: 3
        }} data-id="element-75">
              {success}
            </Alert> : <Alert severity="info" sx={{
          mb: 3
        }} data-id="element-76">
              <Typography variant="caption" data-id="element-77">
                Password must be 8-15 characters with uppercase, lowercase,
                number, and special character.
              </Typography>
            </Alert>}

          <form onSubmit={handleSubmit} data-id="element-78">
            <TextField id="oldPassword" label="Current Password" name="oldPassword" type={showPasswords.old ? 'text' : 'password'} variant="outlined" fullWidth value={formData.oldPassword} onChange={handleChange} disabled={isLoading || !!success} error={!!errors.oldPassword} helperText={errors.oldPassword} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-80">
                    <LockIcon size={20} color="#0052CC" data-id="element-81" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-82">
                    <IconButton onClick={() => togglePasswordVisibility('old')} edge="end" disabled={isLoading || !!success} data-id="element-83">
                      {showPasswords.old ? <EyeOffIcon size={20} data-id="element-84" /> : <EyeIcon size={20} data-id="element-85" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 2.5
          }} data-id="element-79" />

            <TextField id="newPassword" label="New Password" name="newPassword" type={showPasswords.new ? 'text' : 'password'} variant="outlined" fullWidth value={formData.newPassword} onChange={handleChange} disabled={isLoading || !!success} error={!!errors.newPassword} helperText={errors.newPassword} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-87">
                    <LockIcon size={20} color="#0052CC" data-id="element-88" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-89">
                    <IconButton onClick={() => togglePasswordVisibility('new')} edge="end" disabled={isLoading || !!success} data-id="element-90">
                      {showPasswords.new ? <EyeOffIcon size={20} data-id="element-91" /> : <EyeIcon size={20} data-id="element-92" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 2.5
          }} data-id="element-86" />

            <TextField id="confirmPassword" label="Confirm Password" name="confirmPassword" type={showPasswords.confirm ? 'text' : 'password'} variant="outlined" fullWidth value={formData.confirmPassword} onChange={handleChange} disabled={isLoading || !!success} error={!!errors.confirmPassword} helperText={errors.confirmPassword} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-94">
                    <LockIcon size={20} color="#0052CC" data-id="element-95" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-96">
                    <IconButton onClick={() => togglePasswordVisibility('confirm')} edge="end" disabled={isLoading || !!success} data-id="element-97">
                      {showPasswords.confirm ? <EyeOffIcon size={20} data-id="element-98" /> : <EyeIcon size={20} data-id="element-99" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 3
          }} data-id="element-93" />

            <Box sx={{
            display: 'flex',
            gap: 2
          }} data-id="element-100">
              <Button onClick={() => navigate('/')} variant="outlined" fullWidth sx={{
              py: 1.5,
              borderRadius: '6px',
              color: '#6B7280',
              borderColor: '#E5E7EB',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB'
              }
            }} data-id="element-101">
                Cancel
              </Button>
              <Button type="submit" variant="contained" fullWidth disabled={isLoading || !!success || !formData.oldPassword || !formData.newPassword || !formData.confirmPassword} sx={{
              py: 1.5,
              mt: 2,
              mb: 2,
              borderRadius: '6px',
              backgroundColor: '#0052CC',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#003DA8',
                boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
              }
            }} data-id="element-102">
                {isLoading ? <CircularProgress size={24} sx={{
                color: 'white'
              }} data-id="element-103" /> : 'Change Password'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>;
};
export default ChangePasswordPage;